import { useCallback, useEffect, useRef, useState } from 'react';
import { proactiveRequest, type ProactiveGuard } from '@/lib/proactiveAssistant';
import {
  currentHomeSummary, HOME_BRIEFING_TTL, proactiveHomeSummary, type ProactiveHomeSummary,
} from '@/lib/proactiveHomeSummary';

import { observeProactiveVisibility, type ProactiveVisibility } from '@/lib/proactiveVisibility';

export const HOME_BRIEFING_TIMEOUT = 12_000;
const RETURN_DEBOUNCE = 300, RETURN_MIN_INTERVAL = 2_000;
interface SummaryState {
  identity: string;
  status: 'loading' | 'ready' | 'error' | 'disabled';
  summary: ProactiveHomeSummary | null;
}

/** A read-only cache entry point. Returning to Home never prepares or executes anything. */
export function useProactiveHomeSummary(owner: string, guard: ProactiveGuard) {
  const identity = owner + ':' + guard.authSessionId;
  const [state, setState] = useState<SummaryState>({ identity, status: 'loading', summary: null });
  const [clock, setClock] = useState(Date.now);
  const guardRef = useRef(guard);
  guardRef.current = guard;
  const alive = useRef(false), sequence = useRef(0), lastStart = useRef<number | null>(null);
  const visibility = useRef<ProactiveVisibility | null>(null);
  const active = useRef<{ id: number; controller: AbortController } | null>(null);
  const load = useCallback(async (manual = false) => {
    const session = guardRef.current;
    if (!alive.current || !session.isCurrent() || session.signal?.aborted ||
      (manual ? document.visibilityState !== 'visible' : !visibility.current?.isVisible())) return;
    active.current?.controller.abort();
    const id = ++sequence.current, controller = new AbortController();
    const current = () => alive.current && sequence.current === id &&
      !controller.signal.aborted && session.isCurrent() && !session.signal?.aborted;
    active.current = { id, controller };
    lastStart.current = Date.now();
    setState({ identity, status: 'loading', summary: null });
    const abort = () => controller.abort();
    session.signal?.addEventListener('abort', abort, { once: true });
    let timeout: number | undefined;
    try {
      const result = await Promise.race([
        proactiveRequest(owner, { op: 'get' }, { ...session, signal: controller.signal, isCurrent: current }),
        new Promise<never>((_, reject) => {
          timeout = window.setTimeout(() => reject(new Error('timeout')), HOME_BRIEFING_TIMEOUT);
        }),
      ]);
      if (!current()) return;
      if (result.ok !== true || result.user_id !== owner || !result.preferences) {
        throw new Error('request_failed');
      }
      const now = Date.now();
      setClock(now);
      setState({ identity, status: 'ready', summary: proactiveHomeSummary(result, now) });
    } catch (cause) {
      if (!current()) return;
      console.warn('[proactive-home] Cached briefing could not be confirmed');
      setState({
        identity, summary: null,
        status: cause instanceof Error && cause.message === 'proactive_disabled' ? 'disabled' : 'error',
      });
    } finally {
      if (timeout !== undefined) window.clearTimeout(timeout);
      session.signal?.removeEventListener('abort', abort);
      controller.abort();
      if (active.current?.id === id) active.current = null;
    }
  }, [owner, identity]);
  useEffect(() => {
    const activeSequence = sequence;
    alive.current = true;
    lastStart.current = null;
    let debounce: number | undefined;
    const cancel = () => {
      if (debounce !== undefined) window.clearTimeout(debounce);
      debounce = undefined;
    };
    const returned = (reason: 'return' | 'failure') => {
      setClock(Date.now());
      if (reason === 'failure' && !active.current) {
        setState({ identity, status: 'error', summary: null });
      }
      if (!visibility.current?.isVisible()) { cancel(); return; }
      const wait = Math.max(RETURN_DEBOUNCE, lastStart.current === null ? 0
        : RETURN_MIN_INTERVAL - (Date.now() - lastStart.current));
      cancel();
      debounce = window.setTimeout(() => { debounce = undefined; void load(); }, wait);
    };
    const observer = observeProactiveVisibility(returned);
    visibility.current = observer;
    if (!observer.native) void load();
    return () => {
      alive.current = false;
      activeSequence.current++;
      active.current?.controller.abort();
      active.current = null;
      cancel();
      observer.dispose();
      visibility.current = null;
    };
  }, [load, identity]);
  const currentState = state.identity === identity && guard.isCurrent() && !guard.signal?.aborted
    ? state : { identity, status: 'loading' as const, summary: null };
  const summary = currentState.summary ? currentHomeSummary(currentState.summary, clock) : null;
  useEffect(() => {
    if (!summary || summary.generatedAt === null) return;
    const next = Math.min(summary.generatedAt + HOME_BRIEFING_TTL, ...summary.expiresAt);
    const timer = window.setTimeout(() => setClock(Date.now()), Math.max(1, next - Date.now()));
    return () => window.clearTimeout(timer);
  }, [summary]);
  const retry = useCallback(() => { void load(true); }, [load]);
  return { status: currentState.status, summary, retry };
}
